import { type ChangeEvent, type FC, useEffect, useRef, useState } from "react";

import classNames from "classnames";
import identity from "lodash/identity";
import noop from "lodash/noop";

import type { IconName } from "@similarweb/icons";
import type { IAutocompleteProps } from "@similarweb/ui-components";
import { ListItemKeyword, DotsLoader } from "@similarweb/ui-components";

import { keywordGroupsQueryClient } from "app/services/keyword-groups";
import type { IUserKeywordGroup } from "app/services/keyword-groups/keywordGroups.types";
import { useAutoCompleteClick } from "pro-features/components/AutocompleteWebCategories/utils";
import { translate } from "app/filters/i18nFilter";

import { AutocompleteStyled } from "./AutocompleteKeywords";
import { KeywordsGroupSection } from "./components/AutocompleteKeywordGroupsComponents";
import type {
    AutoCompleteOnClick,
    IAutocompleteKeywordGroup,
    ISearchedKeyword,
} from "./types/AutocompleteKeywordGroupTypes";

interface IAutocompleteKeywordsProps {
    selectedValue: string;
    onSelect: AutoCompleteOnClick;
    className?: string;
    textFieldIconName?: IconName;
    keywordsGroupsToFilterFromAutocomplete?: string[];
    keywordsToFilterFromAutocomplete?: string[];
    forceResultsView?: boolean;
    truncateResultsOnBodyClick?: boolean;
    refocusOnItemSelect?: boolean;
    clearTextValueOnItemSelect?: boolean;
    autocompleteProps?: IAutocompleteProps;
    overrideEnterFunc?: (event: FixmeType) => void;
    onAutoCompleteToggle?: (isOpen: boolean) => void;
    onClearSearch?: () => void;
    preventTruncateUnlessForced?: boolean;
}

export const AutoCompleteKeywordGroupsSingleTab: FC<IAutocompleteKeywordsProps> = ({
    selectedValue,
    onSelect,
    className,
    textFieldIconName = "search",
    keywordsGroupsToFilterFromAutocomplete = [],
    keywordsToFilterFromAutocomplete = [],
    forceResultsView = false,
    truncateResultsOnBodyClick = true,
    preventTruncateUnlessForced = true,
    refocusOnItemSelect = true,
    clearTextValueOnItemSelect = true,
    autocompleteProps = {},
    overrideEnterFunc,
    onAutoCompleteToggle = noop,
    onClearSearch = noop,
}) => {
    const selectedValueFormatted =
        typeof selectedValue === "string"
            ? selectedValue
            : ((selectedValue as ISearchedKeyword).name ?? (selectedValue as IUserKeywordGroup).Name);

    const [isInitialQuery, setIsInitialQuery] = useState(true);
    const [isAutocompleteLoading, setIsAutocompleteLoading] = useState(false);
    const [keywordQuery, setKeywordQuery] = useState<string>(selectedValueFormatted);
    const [isDirtyState, setIsDirtyState] = useState(false);

    const autocompleteRef = useRef<FixmeType>(null);
    useAutoCompleteClick(autocompleteRef, truncateResultsOnBodyClick);

    useEffect(() => {
        setKeywordQuery(selectedValueFormatted);
    }, [selectedValueFormatted]);

    /**
     * Resolves the query text that should be sent for the autocomplete search
     */
    const resolveQueryInput = (query: string) => {
        const hasQuery = typeof query === "string" && query !== "";

        if (hasQuery) {
            return query;
        }

        // In case no query is present, but the component user wants to show
        // results immediately, we should use the initial selected value as the query input.
        // this logic is used to display immediate search results upon opening the autocomplete
        // search in the new keywords query bar.
        if (isInitialQuery && forceResultsView) {
            setIsInitialQuery(false);
            return selectedValue;
        }

        return null;
    };

    const getKeywordGroups = async (query: string, hasQuery: boolean): Promise<IAutocompleteKeywordGroup[]> => {
        const keywordGroups = (await keywordGroupsQueryClient.getUserGroups()).map((group) => ({
            ...group,
            SharedWithMe: false,
        }));

        const result = hasQuery
            ? keywordGroups.filter((keywordGroup: IUserKeywordGroup) => {
                  return keywordGroup.Name.toLowerCase().includes(query.toLowerCase());
              })
            : keywordGroups;
        return keywordsGroupsToFilterFromAutocomplete?.length
            ? result.filter(({ Id }) => !keywordsGroupsToFilterFromAutocomplete.includes(Id))
            : result;
    };

    const getSharedKeywordGroups = async (query: string, hasQuery: boolean): Promise<IAutocompleteKeywordGroup[]> => {
        const sharedKwGroups = (await keywordGroupsQueryClient.getSharedGroups()).map((group: IUserKeywordGroup) => ({
            ...group,
            SharedWithMe: true,
        }));

        const result = hasQuery
            ? sharedKwGroups.filter((keywordGroup) => {
                  return keywordGroup.Name.toLowerCase().includes(query.toLowerCase());
              })
            : sharedKwGroups;
        return keywordsGroupsToFilterFromAutocomplete?.length
            ? result.filter(({ Id }) => !keywordsGroupsToFilterFromAutocomplete.includes(Id))
            : result;
    };

    const getAutocompleteData = async (queryInput: string) => {
        setIsAutocompleteLoading(true);

        const query = resolveQueryInput(queryInput) as string;
        let isNextStateDirty = isDirtyState;

        // If the query is the same as the initial query, we should not perform a new search
        // unless we are in a dirty state or the query is empty.
        if (query !== selectedValue || isDirtyState || !query?.length) {
            isNextStateDirty = true;
            setIsDirtyState(true);
            setKeywordQuery(query);
        }

        const searchText = isNextStateDirty ? query : keywordQuery;
        const hasQuery = typeof searchText === "string" && searchText !== "";
        if (!hasQuery && selectedValue) {
            onClearSearch();
        }

        const keywordGroups = await getKeywordGroups(searchText, hasQuery);
        const sharedKeywordGroups = await getSharedKeywordGroups(searchText, hasQuery);
        const isKeywordAlreadyUsed = keywordsToFilterFromAutocomplete?.includes?.(searchText);

        const keywordList: (IAutocompleteKeywordGroup | ISearchedKeyword)[] = [
            ...keywordGroups,
            ...sharedKeywordGroups,
        ];
        if (!isKeywordAlreadyUsed && searchText?.length) {
            keywordList.unshift({ name: searchText });
        }

        setIsAutocompleteLoading(false);
        return keywordList;
    };

    const renderItemsClick = (item: IAutocompleteKeywordGroup) => (e: ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        if (clearTextValueOnItemSelect) {
            setKeywordQuery("");
            autocompleteRef.current.textFieldRef.clearValue();
        }
        if (!refocusOnItemSelect) {
            autocompleteRef?.current?.truncateResults(true);
        } else {
            onSelect(item, e);
        }
    };

    const overrideEnter = (event: FixmeType, selectedItem: FixmeType) => {
        const text = event.target.value;
        if (selectedItem != null) {
            overrideEnterFunc ? overrideEnterFunc?.(selectedItem) : renderItemsClick(selectedItem)(event);
        } else if (text) {
            autocompleteRef?.current?.truncateResults(true);
            const value = event.target.value;
            overrideEnterFunc ? overrideEnterFunc?.(value) : renderItemsClick(value)(event);
        }
        if (clearTextValueOnItemSelect) {
            autocompleteRef.current.textFieldRef.clearValue();
        }
    };

    const renderAutocompleteContent = (data: {
        listItems: (IAutocompleteKeywordGroup | ISearchedKeyword)[];
        selectedItemId: number;
    }) => {
        const { keywords, keywordGroups, sharedKeywordGroups } = data.listItems.reduce(
            (acc, item) => {
                if ("name" in item) {
                    acc.keywords.push(item);
                } else if (item.SharedWithMe) {
                    acc.sharedKeywordGroups.push(item);
                } else {
                    acc.keywordGroups.push(item);
                }
                return acc;
            },
            {
                keywords: [] as ISearchedKeyword[],
                keywordGroups: [] as IAutocompleteKeywordGroup[],
                sharedKeywordGroups: [] as IAutocompleteKeywordGroup[],
            },
        );

        const hasAnyData = keywords?.length > 0 || keywordGroups?.length > 0 || sharedKeywordGroups?.length > 0;

        if (!hasAnyData) {
            return null;
        }

        const hasKeyword = keywords?.[0]?.name?.length > 0;
        const selectedItemId = hasKeyword ? data.selectedItemId - 1 : data.selectedItemId;

        return (
            <div className="ListItemsContainer">
                <KeywordsGroupSection
                    keywordGroups={keywordGroups}
                    sharedKeywordGroups={sharedKeywordGroups}
                    selectedItemId={selectedItemId}
                    translate={translate}
                    renderOnItemClick={renderItemsClick}
                    onEditClick={noop}
                    onShareClick={noop}
                    showEditButton={false}
                    showShareButton={false}
                >
                    {hasKeyword ? (
                        <ListItemKeyword
                            text={keywords[0].name}
                            iconName="search-keywords"
                            isActive={selectedItemId === -1}
                            onClick={(e) => {
                                renderItemsClick(e.target.value)(e);
                            }}
                        />
                    ) : null}
                </KeywordsGroupSection>
            </div>
        );
    };

    const autoCompleteKey = [...keywordsGroupsToFilterFromAutocomplete, ...keywordsToFilterFromAutocomplete].join(",");

    return (
        <AutocompleteStyled
            key={autoCompleteKey}
            className={classNames("AutocompleteWithTabs", className)}
            getListItems={getAutocompleteData}
            getSelectableListItems={identity}
            renderItems={renderAutocompleteContent}
            ref={autocompleteRef}
            loadingComponent={<DotsLoader />}
            isLoading={isAutocompleteLoading}
            debounce={250}
            floating
            searchIcon={textFieldIconName}
            preventTruncateUnlessForced={preventTruncateUnlessForced}
            shouldUpdateSelectedValue
            setIsResultsTruncated={(isTruncated: boolean) => onAutoCompleteToggle(!isTruncated)}
            onBlur={() => setIsDirtyState(false)}
            selectedValue={keywordQuery}
            overrideEnterFunc={overrideEnter}
            {...autocompleteProps}
        />
    );
};
